import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { InfoIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import AnswersNotice from '@/components/ui/answers-notice';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import {
  ACTIVE_ENTITY_CRITERIA,
  ENTITY_CLASSIFICATIONS,
  EntityClassification,
  EXCLUDED_PERSON_TYPES,
  ExcludedPersonType,
  LISTED_CORPORATION_RELATIONSHIPS,
  RegulatedMarketListing,
} from '@/constants/institution';
import { InstitutionFieldsListedCompany } from '@/features/institution/components/fields/listed-company';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import { INSTITUTION_LISTED_COMPANY_DEFAULTS } from '@/features/institution/schemas/listed-company';
import { getInstitutionListedCompanyPrefill, type InstitutionTaxSelfCertificationOrDefaults } from '@/features/institution/tax-self-certification/schema';

const LISTING_EXCLUSIONS: ReadonlyArray<ExcludedPersonType | undefined> = [ExcludedPersonType.LISTED_CORPORATION, ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION];

const InstitutionTaxSelfCertificationFieldsetClassificationLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.classification.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.classification.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetClassificationLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetClassificationLegend';

const InstitutionTaxSelfCertificationFieldsetClassification = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const excludedPersonType = form.watch('excludedPersonType');
  const entityClassification = form.watch('entityClassification');
  const isRelatedEntity = excludedPersonType === ExcludedPersonType.RELATED_ENTITY_OF_LISTED_CORPORATION;
  const isListedCompanyPrefilled = getInstitutionListedCompanyPrefill(excludedPersonType, institutionDetailsAnswers?.ownership) !== undefined;
  const ownershipListing = institutionDetailsAnswers?.ownership?.regulatedMarketListing;
  // Not blocked, as shares on an MTF or a non-EU exchange can count here though not as a regulated market listing in ownership
  const isListedUnlikeOwnership =
    excludedPersonType === ExcludedPersonType.LISTED_CORPORATION && ownershipListing !== undefined && ownershipListing !== RegulatedMarketListing.LISTED;

  // The two claims rest on different listings, so choosing either starts its listing afresh
  const startListingAfresh = (type: ExcludedPersonType) => {
    if (!LISTING_EXCLUSIONS.includes(type)) return;

    const prefill = getInstitutionListedCompanyPrefill(type, institutionDetailsAnswers?.ownership);

    form.setValue('listedCompany', prefill?.listedCompany ?? INSTITUTION_LISTED_COMPANY_DEFAULTS);
    form.setValue('relationship', prefill?.relationship);
    form.setValue('controlOverHalf', false);
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="excludedPersonType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.taxSelfCertification.classification.excludedPersonType')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.taxSelfCertification.classification.excludedPersonType')}
                onValueChange={(type: ExcludedPersonType) => {
                  field.onChange(type);
                  startListingAfresh(type);
                }}
              >
                {EXCLUDED_PERSON_TYPES.map((type) => (
                  <FormItem key={type}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={type} />
                      </FormControl>
                      <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.excludedPersonTypes.${type}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {LISTING_EXCLUSIONS.includes(excludedPersonType) ? (
        <fieldset className="space-y-6">
          <legend className="font-medium">{t('institutionDetails.taxSelfCertification.classification.listedCompany', { context: isRelatedEntity ? 'related' : undefined })}</legend>
          {isListedCompanyPrefilled ? <AnswersNotice icon={InfoIcon}>{t('institutionDetails.taxSelfCertification.classification.listedCompanyPrefilled')}</AnswersNotice> : null}
          {isListedUnlikeOwnership ? (
            <Alert variant="info" role="status">
              <InfoIcon aria-hidden className="size-4" />
              <AlertDescription>
                {t('institutionDetails.taxSelfCertification.classification.listedCompanyNotInOwnership', {
                  context: ownershipListing === RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY ? 'subsidiary' : undefined,
                })}
              </AlertDescription>
            </Alert>
          ) : null}
          <InstitutionFieldsListedCompany isNameAsked={isRelatedEntity} isMicRequired micDescription={t('institutionDetails.taxSelfCertification.classification.micDescription')} />
        </fieldset>
      ) : null}
      {isRelatedEntity ? (
        <>
          <FormField
            control={form.control}
            name="relationship"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.taxSelfCertification.classification.relationship')}*</FormLabel>
                <FormDescription>{t('institutionDetails.taxSelfCertification.classification.relationshipDescription')}</FormDescription>
                <FormControl>
                  <RadioGroup
                    value={field.value ?? ''}
                    variant="cards"
                    aria-label={t('institutionDetails.taxSelfCertification.classification.relationship')}
                    onValueChange={field.onChange}
                  >
                    {LISTED_CORPORATION_RELATIONSHIPS.map((relationship) => (
                      <FormItem key={relationship}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={relationship} />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.relationships.${relationship}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="controlOverHalf"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.classification.controlOverHalf')}</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}
      {excludedPersonType === ExcludedPersonType.NONE ? (
        <>
          <FormField
            control={form.control}
            name="entityClassification"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.taxSelfCertification.classification.entityClassification')}*</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.taxSelfCertification.classification.entityClassification')}
                    onValueChange={field.onChange}
                  >
                    {ENTITY_CLASSIFICATIONS.map((classification) => (
                      <FormItem key={classification}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={classification} />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.entityClassifications.${classification}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {entityClassification === EntityClassification.ACTIVE_ENTITY ? (
            <FormField
              control={form.control}
              name="activeEntityCriterion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.taxSelfCertification.classification.activeEntityCriterion')}*</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      variant="cards"
                      aria-label={t('institutionDetails.taxSelfCertification.classification.activeEntityCriterion')}
                      onValueChange={field.onChange}
                    >
                      {ACTIVE_ENTITY_CRITERIA.map((criterion) => (
                        <FormItem key={criterion}>
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={criterion} />
                            </FormControl>
                            <FormLabel contained>{t(`institutionDetails.taxSelfCertification.classification.activeEntityCriteria.${criterion}`)}</FormLabel>
                            <FormDescription className="text-muted-foreground peer-aria-[checked=true]:text-primary">
                              {t(`institutionDetails.taxSelfCertification.classification.activeEntityCriteriaDescriptions.${criterion}`)}
                            </FormDescription>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
};
InstitutionTaxSelfCertificationFieldsetClassification.displayName = 'InstitutionTaxSelfCertificationFieldsetClassification';

export { InstitutionTaxSelfCertificationFieldsetClassification, InstitutionTaxSelfCertificationFieldsetClassificationLegend };
